import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, BarChart, Bar, CartesianGrid, LineChart, Line, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Star, StarOff, Heart, Repeat2, MessageCircle, ExternalLink, RefreshCw, ShieldCheck, Users, Search, ArrowUpDown, TrendingUp, Loader2, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { useIndiaCompanyStore } from '../../../state/indiaCompanyStore';
import { useWatchlistStore } from '../../../state/watchlistStore';
import { useAuthStore } from '../../../state/authStore';
import { useIndiaDocsStore } from '../../../state/indiaDocsStore';
import { useXSignalsStore } from '../../../state/xSignalsStore';
import { useViewStore } from '../../../state/viewStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { Skeleton } from '../../../components/ui/skeleton';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import ShareToChat from '../../../components/ui/ShareToChat';
import IntelPanel from '../../../components/intel/IntelPanel';
import ChartPanel from '../../../components/charts/ChartPanel';
import VerifiedFinancials from '../../../components/financials/VerifiedFinancials';

// ─── Helpers ────────────────────────────────────────────

const fmt = (v, decimals = 2) => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('en-IN', { maximumFractionDigits: decimals });
};

const fmtCr = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  if (Math.abs(n) >= 1e12) return `₹${(n / 1e7 / 1e5).toFixed(2)} L Cr`;
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${fmt(n)}`;
};

const pct = (v) => (v == null ? '—' : `${(Number(v) * 100).toFixed(2)}%`);

const pctRaw = (v) => (v == null ? '—' : `${Number(v).toFixed(2)}%`);
const INDIA_API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

// ─── Metric Card ────────────────────────────────────────

function MetricCard({ label, value, sub }) {
  return (
    <Card className="bg-muted/30">
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className="text-lg font-semibold">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Price Chart ────────────────────────────────────────

function PriceChart({ data, height = 300 }) {
  if (!data || data.length === 0) return <p className="text-muted-foreground text-sm">No price data</p>;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5)} tick={{ fontSize: 11 }} />
        <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} tickFormatter={(v) => fmt(v, 0)} />
        <RechartsTooltip
          contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
          formatter={(v) => [`₹${fmt(v)}`, 'Close']}
        />
        <Area type="monotone" dataKey="close" stroke="hsl(var(--primary))" fillOpacity={1} fill="url(#priceGrad)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ─── 52-week bar ────────────────────────────────────────

function FiftyTwoWeekBar({ low, high, current }) {
  if (!low || !high) return null;
  const range = high - low;
  const pos = range > 0 ? ((current - low) / range) * 100 : 50;
  return (
    <div className="mt-2">
      <div className="flex justify-between text-xs text-muted-foreground mb-1">
        <span>52W Low: ₹{fmt(low)}</span>
        <span>52W High: ₹{fmt(high)}</span>
      </div>
      <div className="relative h-2 bg-muted rounded-full">
        <div className="absolute h-2 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full" style={{ width: '100%' }} />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-primary rounded-full"
          style={{ left: `${Math.min(Math.max(pos, 0), 100)}%`, transform: 'translate(-50%, -50%)' }}
        />
      </div>
    </div>
  );
}

// ─── Financial Table ────────────────────────────────────

function FinancialTable({ data, title }) {
  if (!data || Object.keys(data).length === 0) {
    return <p className="text-muted-foreground text-sm py-4">No {title?.toLowerCase()} data available.</p>;
  }
  const dates = Object.keys(data).sort().reverse();
  const items = [...new Set(dates.flatMap((d) => Object.keys(data[d])))];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 pr-4 font-medium text-muted-foreground sticky left-0 bg-background min-w-[200px]">
              {title}
            </th>
            {dates.map((d) => (
              <th key={d} className="text-right py-2 px-3 font-medium text-muted-foreground whitespace-nowrap">
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item} className="border-b border-muted/50 hover:bg-muted/30">
              <td className="py-1.5 pr-4 sticky left-0 bg-background">{item}</td>
              {dates.map((d) => (
                <td key={d} className="text-right py-1.5 px-3 whitespace-nowrap">
                  {fmtCr(data[d]?.[item])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Structured Shareholding ─────────────────────────────

function ShareholdingSection({ pattern, details, promoters, directors, majorHolders, institutionalHolders, shareholdingFilings }) {
  const latest = pattern?.[0] || null;
  const n = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };
  const totalPct = (q) => (
    n(q?.promoter_pct) +
    n(q?.fii_pct) +
    n(q?.dii_pct) +
    n(q?.public_pct) +
    n(q?.government_pct) +
    n(q?.others_pct)
  );
  const latestTotal = latest ? totalPct(latest) : 0;
  const pieDataRaw = latest ? [
    { name: 'Promoter', value: n(latest.promoter_pct), color: '#16a34a' },
    { name: 'FII', value: n(latest.fii_pct), color: '#2563eb' },
    { name: 'DII', value: n(latest.dii_pct), color: '#7c3aed' },
    { name: 'Public', value: n(latest.public_pct), color: '#f59e0b' },
    { name: 'Government', value: n(latest.government_pct), color: '#ef4444' },
    { name: 'Others', value: n(latest.others_pct), color: '#64748b' },
  ].filter((x) => x.value > 0) : [];
  const pieData = latestTotal > 0
    ? pieDataRaw.map((x) => ({ ...x, value: (x.value * 100) / latestTotal }))
    : [];

  const latestQuarter = latest?.quarter_end || null;
  const quarterLabel = latestQuarter ? new Date(latestQuarter).toLocaleDateString() : 'Latest';
  const institutionsFromDetails = (details || []).filter(
    (d) => d.holder_category === 'FII' || d.holder_category === 'DII',
  );

  const fallbackOld = !pattern?.length && !details?.length;

  return (
    <div className="space-y-6">
      {!fallbackOld && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Shareholding Mix ({quarterLabel})</CardTitle>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={92} label={(e) => `${e.name}: ${e.value.toFixed(2)}%`}>
                          {pieData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Legend />
                        <RechartsTooltip formatter={(v) => `${Number(v).toFixed(2)}%`} />
                      </PieChart>
                    </ResponsiveContainer>
                    <p className="text-xs text-muted-foreground mt-2">
                      Raw total from source: {latestTotal.toFixed(2)}%. Chart is normalized to 100% for readability.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">No latest shareholding mix available yet.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Promoters (Top Holders)</CardTitle>
              </CardHeader>
              <CardContent>
                {promoters?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 pr-3 font-medium text-muted-foreground">Promoter</th>
                          <th className="text-left py-2 px-3 font-medium text-muted-foreground">DIN</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">% Held</th>
                          <th className="text-right py-2 px-3 font-medium text-muted-foreground">Shares</th>
                        </tr>
                      </thead>
                      <tbody>
                        {promoters.slice(0, 20).map((p, i) => (
                          <tr key={`${p.holder_name}-${i}`} className="border-b border-muted/50">
                            <td className="py-1.5 pr-3">{p.holder_name}</td>
                            <td className="py-1.5 px-3">
                              {p.din ? (
                                <a href={`${INDIA_API}/director/${encodeURIComponent(p.din)}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-700">
                                  {p.din}
                                </a>
                              ) : '—'}
                            </td>
                            <td className="text-right py-1.5 px-3">{pctRaw(p.pct_held)}</td>
                            <td className="text-right py-1.5 px-3">{fmt(p.shares_held, 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Promoter-holder level data not available yet.</p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Quarterly Shareholding Trend</CardTitle>
            </CardHeader>
            <CardContent>
              {pattern?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-3 font-medium text-muted-foreground">Quarter</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Promoter</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">FII</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">DII</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Public</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Government</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Others</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Total</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Shareholders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pattern.slice(0, 8).map((q) => (
                        <tr key={q.quarter_end} className="border-b border-muted/50">
                          <td className="py-1.5 pr-3">{q.quarter_end ? new Date(q.quarter_end).toLocaleDateString() : '—'}</td>
                          <td className="text-right py-1.5 px-3">{pctRaw(q.promoter_pct)}</td>
                          <td className="text-right py-1.5 px-3">{pctRaw(q.fii_pct)}</td>
                          <td className="text-right py-1.5 px-3">{pctRaw(q.dii_pct)}</td>
                          <td className="text-right py-1.5 px-3">{pctRaw(q.public_pct)}</td>
                          <td className="text-right py-1.5 px-3">{pctRaw(q.government_pct)}</td>
                          <td className="text-right py-1.5 px-3">{pctRaw(q.others_pct)}</td>
                          <td className="text-right py-1.5 px-3 font-medium">{pctRaw(totalPct(q))}</td>
                          <td className="text-right py-1.5 px-3">{fmt(q.num_shareholders, 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Quarterly shareholding trend not available yet.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Institutional Holders (FII/DII)</CardTitle>
            </CardHeader>
            <CardContent>
              {institutionsFromDetails.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-3 font-medium text-muted-foreground">Holder</th>
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">Category</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">% Held</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Shares</th>
                      </tr>
                    </thead>
                    <tbody>
                      {institutionsFromDetails.slice(0, 30).map((h, i) => (
                        <tr key={`${h.holder_name}-${i}`} className="border-b border-muted/50">
                          <td className="py-1.5 pr-3">{h.holder_name}</td>
                          <td className="py-1.5 px-3">{h.holder_category}</td>
                          <td className="text-right py-1.5 px-3">{pctRaw(h.pct_held)}</td>
                          <td className="text-right py-1.5 px-3">{fmt(h.shares_held, 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Institutional holder-level data not available yet.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Directors and DIN</CardTitle>
            </CardHeader>
            <CardContent>
              {directors?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-3 font-medium text-muted-foreground">Name</th>
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">DIN</th>
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">Designation</th>
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">Appointed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {directors.map((d) => (
                        <tr key={`${d.din}-${d.name}`} className="border-b border-muted/50">
                          <td className="py-1.5 pr-3">{d.name}</td>
                          <td className="py-1.5 px-3">
                            <a
                              href={`${INDIA_API}/director/${encodeURIComponent(d.din)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-emerald-600 hover:text-emerald-700"
                            >
                              {d.din}
                            </a>
                          </td>
                          <td className="py-1.5 px-3">{d.designation || '—'}</td>
                          <td className="py-1.5 px-3">{d.appointment_date ? new Date(d.appointment_date).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Director DIN data not available yet.</p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {fallbackOld && (
        <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Shareholding Data Source Status</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Structured quarterly shareholding data is not available for this company right now.
                We are showing latest ownership proxy data and exchange filings below.
              </p>
            </CardContent>
          </Card>
          {shareholdingFilings && shareholdingFilings.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Latest Shareholding Filings</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {shareholdingFilings.slice(0, 12).map((f, i) => (
                    <div key={i} className="border rounded-md p-3 text-sm hover:bg-muted/40">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium">{f.headline || 'Shareholding filing'}</div>
                        {f.attachment_url && (
                          <a href={f.attachment_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:text-emerald-700 whitespace-nowrap">
                            Open PDF ↗
                          </a>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {f.date ? new Date(f.date).toLocaleDateString() : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {majorHolders && majorHolders.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Major Holders (Fallback)</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <tbody>
                    {majorHolders.map((h, i) => {
                      const label = String(h?.index ?? h?.label ?? Object.keys(h || {})[0] ?? 'Metric');
                      const rawVal = h?.Value ?? h?.value ?? Object.values(h || {})[1];
                      const isCount = /count|number/i.test(label);
                      const displayVal = rawVal == null
                        ? '—'
                        : (isCount ? fmt(rawVal, 0) : pctRaw(rawVal));
                      return (
                        <tr key={i} className="border-b border-muted/50">
                          <td className="py-1.5 pr-4">{label}</td>
                          <td className="py-1.5 pr-4 text-right">{displayVal}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
          {institutionalHolders && institutionalHolders.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Institutional Holders (Fallback)</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-3 font-medium text-muted-foreground">Holder</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Shares</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">% Out</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {institutionalHolders.map((h, i) => (
                        <tr key={i} className="border-b border-muted/50 hover:bg-muted/30">
                          <td className="py-1.5 pr-3">{h.Holder || h.holder || '—'}</td>
                          <td className="text-right py-1.5 px-3">{fmt(h.Shares || h.shares, 0)}</td>
                          <td className="text-right py-1.5 px-3">{pct(h['% Out'] || h.pctHeld)}</td>
                          <td className="text-right py-1.5 px-3">{fmtCr(h.Value || h.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
          {(!majorHolders || majorHolders.length === 0) && (!institutionalHolders || institutionalHolders.length === 0) && (
            <p className="text-muted-foreground text-sm">Shareholding data not available.</p>
          )}
        </>
      )}
    </div>
  );
}

function CreditRatingsSection({ ratings }) {
  if (!ratings || ratings.length === 0) {
    return <p className="text-sm text-muted-foreground">No credit ratings filings available yet.</p>;
  }
  const indian = ratings.filter((r) => r.agency_scope === 'INDIA');
  const global = ratings.filter((r) => r.agency_scope === 'GLOBAL');
  const unknown = ratings.filter((r) => !r.agency_scope);
  const ordered = [...indian, ...global, ...unknown];
  return (
    <div className="space-y-4">
      <div className="text-xs text-muted-foreground">
        Indian agencies: {indian.length} · Global agencies: {global.length} · Other: {unknown.length}
      </div>
      <div className="space-y-2">
        {ordered.map((r, i) => (
          <div key={`${r.headline}-${i}`} className="border rounded-md p-3 text-sm hover:bg-muted/40">
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={r.agency_scope === 'GLOBAL' ? 'secondary' : 'default'} className="text-[10px]">
                    {r.agency || 'Rating Agency'}
                  </Badge>
                  {r.agency_scope && <Badge variant="outline" className="text-[10px]">{r.agency_scope}</Badge>}
                  {r.rating_text && <Badge variant="outline" className="text-[10px]">{r.rating_text}</Badge>}
                </div>
                <div className="font-medium">{r.headline || 'Credit rating update'}</div>
                <div className="text-xs text-muted-foreground">
                  {r.date ? new Date(r.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                </div>
              </div>
              {r.attachment_url && (
                <a href={r.attachment_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:text-emerald-700 whitespace-nowrap shrink-0">
                  Report ↗
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Corporate Info (MCA) ────────────────────────────────

function CorporateInfoSection({ mcaData, charges, directors, db }) {
  const mca = mcaData || {};
  const hasMca = mca.cin || mca.company_name_mca || mca.incorporation_date;

  const infoRows = [
    { label: 'CIN', value: mca.cin },
    { label: 'MCA Company Name', value: mca.company_name_mca },
    { label: 'Incorporation Date', value: mca.incorporation_date ? new Date(mca.incorporation_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null },
    { label: 'Registered Address', value: mca.registered_address },
    { label: 'State', value: mca.registered_state },
    { label: 'PIN Code', value: mca.registered_pin },
    { label: 'ROC Code', value: mca.roc_code },
    { label: 'Company Status', value: mca.company_status },
    { label: 'Company Class', value: mca.company_class },
    { label: 'Company Type', value: mca.company_type },
    { label: 'Authorized Capital', value: mca.authorized_capital != null ? `₹${Number(mca.authorized_capital).toLocaleString('en-IN')}` : null },
    { label: 'Paid-up Capital', value: mca.paid_up_capital != null ? `₹${Number(mca.paid_up_capital).toLocaleString('en-IN')}` : null },
    { label: 'Activity Code (NIC)', value: mca.activity_code },
    { label: 'Activity Description', value: mca.activity_description },
    { label: 'Email', value: mca.company_email },
    { label: 'Last AGM', value: mca.last_agm_date ? new Date(mca.last_agm_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null },
    { label: 'Last Balance Sheet', value: mca.last_balance_sheet_date ? new Date(mca.last_balance_sheet_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null },
    { label: 'Last Annual Filing', value: mca.last_annual_filing_date ? new Date(mca.last_annual_filing_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null },
    { label: 'Compliance Status', value: mca.compliance_for_filing },
    { label: 'CIRP Status', value: mca.cirp_status },
    { label: 'Number of Members', value: mca.number_of_members != null ? Number(mca.number_of_members).toLocaleString('en-IN') : null },
    { label: 'Number of Charges', value: mca.number_of_charges != null ? String(mca.number_of_charges) : null },
  ].filter((r) => r.value);

  const statusColor = (s) => {
    if (!s) return '';
    const sl = s.toLowerCase();
    if (sl === 'active' || sl === 'active-compliant') return 'text-emerald-600';
    if (sl.includes('struck') || sl === 'dormant') return 'text-red-600';
    return 'text-amber-600';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">MCA Registration Details</CardTitle>
        </CardHeader>
        <CardContent>
          {hasMca ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              {infoRows.map((r) => (
                <div key={r.label} className="flex justify-between py-1.5 border-b border-muted/40 text-sm">
                  <span className="text-muted-foreground">{r.label}</span>
                  <span className={`font-medium text-right max-w-[60%] ${r.label === 'Company Status' ? statusColor(r.value) : ''}`}>
                    {r.value}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground space-y-2">
              <p>MCA registration data is not available for this company yet.</p>
              <p className="text-xs">CIN lookup and MCA enrichment need to be run first. Check the ingestion status in the Documents tab.</p>
            </div>
          )}
          {mca.source_name && (
            <p className="text-xs text-muted-foreground mt-4">
              Source: {mca.source_name} · Last fetched: {mca.fetched_at ? new Date(mca.fetched_at).toLocaleDateString('en-IN') : 'Unknown'}
            </p>
          )}
        </CardContent>
      </Card>

      {charges && charges.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Charges / Loans / Mortgages ({charges.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-3 font-medium text-muted-foreground">Holder</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Created</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Modified</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Satisfied</th>
                    <th className="text-right py-2 px-3 font-medium text-muted-foreground">Amount</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map((c, i) => (
                    <tr key={c.charge_id || i} className="border-b border-muted/50 hover:bg-muted/30">
                      <td className="py-1.5 pr-3">{c.holder_name || '—'}</td>
                      <td className="py-1.5 px-3">{c.date_of_creation ? new Date(c.date_of_creation).toLocaleDateString() : '—'}</td>
                      <td className="py-1.5 px-3">{c.date_of_modification ? new Date(c.date_of_modification).toLocaleDateString() : '—'}</td>
                      <td className="py-1.5 px-3">{c.date_of_satisfaction ? new Date(c.date_of_satisfaction).toLocaleDateString() : '—'}</td>
                      <td className="py-1.5 px-3 text-right font-mono">
                        {c.amount != null ? `₹${Number(c.amount).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td className="py-1.5 px-3">
                        <Badge variant={c.status?.toLowerCase() === 'satisfied' ? 'default' : 'secondary'} className="text-xs">
                          {c.status || '—'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {directors && directors.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Board of Directors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-3 font-medium text-muted-foreground">Name</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">DIN</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Designation</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Appointed</th>
                  </tr>
                </thead>
                <tbody>
                  {directors.map((d) => (
                    <tr key={`${d.din}-${d.name}`} className="border-b border-muted/50 hover:bg-muted/30">
                      <td className="py-1.5 pr-3 font-medium">{d.name}</td>
                      <td className="py-1.5 px-3 font-mono text-xs">{d.din || '—'}</td>
                      <td className="py-1.5 px-3">{d.designation || '—'}</td>
                      <td className="py-1.5 px-3">{d.appointment_date ? new Date(d.appointment_date).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Recommendations Chart ──────────────────────────────

function RecommendationsChart({ data }) {
  if (!data || data.length === 0) return <p className="text-muted-foreground text-sm">No recommendations data.</p>;
  const mapped = data.map((r) => ({
    period: r.period || r.Date || '—',
    strongBuy: r.strongBuy || r['Strong Buy'] || 0,
    buy: r.buy || r.Buy || 0,
    hold: r.hold || r.Hold || 0,
    sell: r.sell || r.Sell || 0,
    strongSell: r.strongSell || r['Strong Sell'] || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={mapped}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis dataKey="period" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <RechartsTooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
        <Bar dataKey="strongBuy" stackId="a" fill="#16a34a" name="Strong Buy" />
        <Bar dataKey="buy" stackId="a" fill="#4ade80" name="Buy" />
        <Bar dataKey="hold" stackId="a" fill="#facc15" name="Hold" />
        <Bar dataKey="sell" stackId="a" fill="#f97316" name="Sell" />
        <Bar dataKey="strongSell" stackId="a" fill="#ef4444" name="Strong Sell" />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Peers Table ────────────────────────────────────────

function PeersTable({ peers }) {
  const navigate = useNavigate();
  if (!peers || peers.length === 0) return <p className="text-muted-foreground text-sm">No peers found in the same sector.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 pr-3 font-medium text-muted-foreground">Company</th>
            <th className="text-left py-2 px-3 font-medium text-muted-foreground">Symbol</th>
            <th className="text-right py-2 px-3 font-medium text-muted-foreground">Price</th>
            <th className="text-right py-2 px-3 font-medium text-muted-foreground">Market Cap</th>
          </tr>
        </thead>
        <tbody>
          {peers.map((p) => {
            const peerId = p.nse_symbol || p.bse_code || p.isin;
            return (
            <tr
              key={p.isin}
              className="border-b border-muted/50 hover:bg-muted/30 cursor-pointer"
              onClick={() => { if (peerId) navigate(`/public/india-company/${peerId}`); }}
            >
              <td className="py-1.5 pr-3 font-medium">{p.company_name}</td>
              <td className="py-1.5 px-3">
                {p.nse_symbol ? (
                  <span className="text-emerald-600">{p.nse_symbol}</span>
                ) : p.bse_code ? (
                  <span className="text-blue-600">BSE:{p.bse_code}</span>
                ) : '—'}
              </td>
              <td className="py-1.5 px-3 text-right font-mono">
                {p.current_price != null ? `₹${p.current_price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
              </td>
              <td className="py-1.5 px-3 text-right font-mono">
                {p.market_cap != null ? fmtCr(p.market_cap) : '—'}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Revenue / Profit mini-chart from financials ────────

function RevenueProfitChart({ financials }) {
  if (!financials || Object.keys(financials).length === 0) return null;
  const dates = Object.keys(financials).sort();
  const chartData = dates.map((d) => {
    const col = financials[d] || {};
    return {
      date: d,
      revenue: col['Total Revenue'] || col['Operating Revenue'] || null,
      profit: col['Net Income'] || col['Net Income From Continuing Operation Net Minority Interest'] || null,
    };
  });
  if (chartData.every((d) => !d.revenue && !d.profit)) return null;
  return (
    <Card className="mt-4">
      <CardHeader className="pb-2"><CardTitle className="text-base">Revenue & Profit Trend</CardTitle></CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v) => fmtCr(v)} tick={{ fontSize: 11 }} />
            <RechartsTooltip
              contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
              formatter={(v) => [fmtCr(v)]}
            />
            <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" name="Revenue" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="profit" stroke="#4ade80" name="Net Profit" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ═════════════════════════════════════════════════════════
//  MAIN PAGE
// ═════════════════════════════════════════════════════════

const OUTLOOK_STYLES = {
  bullish: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', border: 'border-emerald-500/30', label: 'Bullish' },
  neutral: { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-500/30', label: 'Neutral' },
  bearish: { bg: 'bg-red-500/10', text: 'text-red-600', border: 'border-red-500/30', label: 'Bearish' },
};

function AIViewCard({ isin, symbol, companyName }) {
  const { currentView, viewHistory, loading, generating, fetchViewHistory, generateView } = useViewStore();
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (isin) fetchViewHistory(isin);
  }, [isin, fetchViewHistory]);

  const outlook = OUTLOOK_STYLES[currentView?.outlook] || OUTLOOK_STYLES.neutral;

  return (
    <Card className={`border ${currentView ? outlook.border : 'border-border'}`}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className={`w-4 h-4 ${currentView ? outlook.text : 'text-muted-foreground'}`} />
            <h3 className="text-sm font-semibold">AI View</h3>
            {currentView && (
              <Badge className={`text-[10px] ${outlook.bg} ${outlook.text}`}>{outlook.label}</Badge>
            )}
            {currentView?.confidence && (
              <Badge className="text-[10px] bg-gray-500/10 text-gray-500">
                {currentView.confidence} confidence
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {viewHistory.length > 1 && (
              <button onClick={() => setShowHistory(!showHistory)}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5">
                History {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            )}
            <Button size="sm" variant="outline" className="h-7 text-xs"
              disabled={generating} onClick={() => generateView(isin)}>
              {generating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              {generating ? 'Generating...' : currentView ? 'Refresh' : 'Generate'}
            </Button>
          </div>
        </div>

        {!currentView && !generating && !loading && (
          <p className="text-xs text-muted-foreground">
            No AI view generated yet. Click "Generate" to get an AI assessment of {companyName}.
          </p>
        )}

        {loading && !currentView && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3 h-3 animate-spin" />Loading view...
          </div>
        )}

        {currentView && (
          <div className="space-y-2">
            {currentView.target_price_range && Object.keys(currentView.target_price_range).length > 0 && (
              <div className="flex items-center gap-4 text-xs">
                <span className="text-muted-foreground">Target:</span>
                {currentView.target_price_range.low && <span>Low: ₹{currentView.target_price_range.low}</span>}
                {currentView.target_price_range.mid && <span className="font-semibold">Mid: ₹{currentView.target_price_range.mid}</span>}
                {currentView.target_price_range.high && <span>High: ₹{currentView.target_price_range.high}</span>}
              </div>
            )}

            {currentView.key_drivers?.length > 0 && (
              <div>
                <p className="text-[10px] text-muted-foreground font-semibold mb-0.5">Key Drivers</p>
                <ul className="text-xs space-y-0.5">
                  {currentView.key_drivers.slice(0, 3).map((d, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <TrendingUp className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {currentView.key_risks?.length > 0 && (
              <div>
                <p className="text-[10px] text-muted-foreground font-semibold mb-0.5">Key Risks</p>
                <ul className="text-xs space-y-0.5">
                  {currentView.key_risks.slice(0, 3).map((r, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <ShieldCheck className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {currentView.change_narrative && (
              <div className="mt-2 p-2 bg-amber-500/5 rounded-md border border-amber-500/20">
                <p className="text-[10px] text-amber-600 font-semibold mb-0.5">What Changed</p>
                <p className="text-xs">{currentView.change_narrative}</p>
              </div>
            )}

            {currentView.created_at && (
              <p className="text-[10px] text-muted-foreground">
                Last updated: {new Date(currentView.created_at || currentView.view_date).toLocaleDateString()}
              </p>
            )}
          </div>
        )}

        {showHistory && viewHistory.length > 1 && (
          <div className="mt-3 pt-3 border-t space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground">View History</h4>
            {viewHistory.slice(1).map((v, i) => {
              const vStyle = OUTLOOK_STYLES[v.outlook] || OUTLOOK_STYLES.neutral;
              return (
                <div key={v.id || i} className="flex items-center gap-2 text-xs">
                  <div className={`w-1.5 h-1.5 rounded-full ${vStyle.bg.replace('/10', '')}`} />
                  <Badge className={`text-[9px] ${vStyle.bg} ${vStyle.text}`}>{vStyle.label}</Badge>
                  <span className="text-muted-foreground">{v.confidence}</span>
                  <span className="text-muted-foreground ml-auto">
                    {v.view_date || (v.created_at ? new Date(v.created_at).toLocaleDateString() : '')}
                  </span>
                  {v.change_narrative && (
                    <span className="text-[10px] text-amber-600 truncate max-w-[200px]">{v.change_narrative}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TeamNotesTab({ isin, symbol }) {
  const user = useAuthStore((s) => s.user);
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [consensus, setConsensus] = useState(null);
  const [rating, setRating] = useState('hold');
  const [conviction, setConviction] = useState(3);
  const [rationale, setRationale] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/orgs`).then(({ data }) => {
      const o = Array.isArray(data) ? data : [];
      setOrgs(o);
      if (o[0]?.id) setOrgId(o[0].id);
    }).catch(() => {});
  }, [user?.id]);

  const load = useCallback(async () => {
    if (!orgId || !isin) return;
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/research-notes`, {
        params: { org_id: orgId, isin },
      });
      setNotes(Array.isArray(data) ? data : []);
    } catch {
      setNotes([]);
    }
  }, [orgId, isin]);

  useEffect(() => { load(); }, [load]);

  const loadConsensus = useCallback(async () => {
    if (!orgId || !isin) return;
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/internal-ratings/consensus`, {
        params: { org_id: orgId, isin },
      });
      setConsensus(data);
    } catch {
      setConsensus(null);
    }
  }, [orgId, isin]);

  useEffect(() => { loadConsensus(); }, [loadConsensus]);

  if (!isin) return <p className="text-sm text-muted-foreground">No ISIN for this listing.</p>;
  if (!orgId) return <p className="text-sm text-muted-foreground">Join an organization (Org settings) to use team notes.</p>;

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex gap-2 items-center text-sm">
        <span className="text-muted-foreground">Org:</span>
        <select className="rounded-md border bg-background px-2 py-1" value={orgId} onChange={e => setOrgId(e.target.value)}>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">New note</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <Input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <textarea className="w-full min-h-[100px] rounded-md border bg-background px-3 py-2 text-sm" placeholder="Markdown supported" value={content} onChange={(e) => setContent(e.target.value)} />
          <select className="rounded-md border bg-background px-2 py-1 text-sm" value={noteType} onChange={(e) => setNoteType(e.target.value)}>
            {['general', 'thesis', 'earnings', 'risk', 'catalyst'].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <Button size="sm" className="bg-emerald-600" onClick={async () => {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/research-notes`, {
              org_id: orgId, isin, symbol, title, content_md: content, note_type: noteType,
            });
            setTitle('');
            setContent('');
            load();
          }}>Add note</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Internal rating</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          {consensus && (
            <p className="text-xs text-muted-foreground">
              Avg conviction: {consensus.avg_conviction ?? '—'} · Team notes: {consensus.ratings?.length || 0}
            </p>
          )}
          <div className="flex flex-wrap gap-2 items-center">
            <select className="rounded-md border bg-background px-2 py-1 text-xs" value={rating} onChange={e => setRating(e.target.value)}>
              {['strong_buy', 'buy', 'hold', 'sell', 'strong_sell'].map((r) => (
                <option key={r} value={r}>{r.replace('_', ' ')}</option>
              ))}
            </select>
            <label className="text-xs text-muted-foreground flex items-center gap-1">
              Conviction
              <input type="range" min={1} max={5} value={conviction} onChange={e => setConviction(Number(e.target.value))} />
              {conviction}
            </label>
          </div>
          <Input placeholder="Rationale (optional)" value={rationale} onChange={e => setRationale(e.target.value)} />
          <Button size="sm" variant="secondary" onClick={async () => {
            await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/internal-ratings`, {
              org_id: orgId, isin, symbol, rating, conviction, rationale,
            });
            loadConsensus();
          }}>Save my rating</Button>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {notes.map((n) => (
          <Card key={n.id}>
            <CardContent className="pt-3 text-sm">
              <div className="flex justify-between gap-2">
                <p className="font-medium">{n.title || 'Note'}</p>
                <Badge variant="outline" className="text-[10px]">{n.note_type}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">{n.user_name} · {n.created_at}</p>
              <p className="mt-2 whitespace-pre-wrap text-muted-foreground">{n.content_md}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function IndiaCompanyPage() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { companyData, loading, error, activeTab, fetchCompany, setTab } = useIndiaCompanyStore();
  const [searchParams] = useSearchParams();
  const { isInWatchlist, addToWatchlist, removeFromWatchlist, getWatchlistItem } = useWatchlistStore();
  const {
    documents,
    transcripts,
    bseNotices,
    bseNoticesGrouped,
    loadingDocuments,
    loadingTranscripts,
    loadingBseNotices,
    fetchDocuments,
    fetchEarningsTranscripts,
    fetchBseNotices,
    startIngestion,
  } = useIndiaDocsStore();
  const {
    posts: xPosts,
    loading: xLoading,
    refreshing: xRefreshing,
    error: xError,
    lastFetched: xLastFetched,
    sourceCount: xSourceCount,
    feedMode: xFeedMode,
    setFeedMode: setXFeedMode,
    fetchFeed: fetchXFeed,
    refreshFeed: refreshXFeed,
  } = useXSignalsStore();

  useEffect(() => {
    if (symbol) fetchCompany(symbol);
    // Command-palette deep links: /public/india-company/RELIANCE?tab=intel
    // lands straight on the requested tab. Runs in the same effect as
    // fetchCompany so we don't add a second useEffect and avoid the
    // CRA exhaustive-deps rule noise.
    const requested = searchParams.get('tab');
    if (requested) {
      const allowed = new Set([
        'overview', 'intel', 'chart', 'financials', 'corporate-info',
        'credit-ratings', 'peers', 'corporate-actions', 'insider',
        'bse-notices', 'documents', 'earnings', 'xsignals', 'team-notes',
      ]);
      if (allowed.has(requested)) setTab(requested);
    }
    return () => useIndiaCompanyStore.getState().reset();
  }, [symbol, fetchCompany, searchParams, setTab]);

  if (loading) {
    return (
      <div className="p-6 space-y-4 max-w-7xl mx-auto">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <div className="grid grid-cols-4 gap-4 mt-6">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
        <Skeleton className="h-64 mt-6" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto text-center">
        <p className="text-destructive text-lg mb-4">{error}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  if (!companyData) return null;

  const info = companyData.info || {};
  const db = companyData.db || {};
  const vf = companyData.verified_financials || {};
  const isVerified = !!vf.verified;
  const vfRatios = vf.ratios || {};
  const vfSnap = vf.snapshot || {};
  const vfLatest = (vf.annual && vf.annual[0]) || {};
  const stockSymbol = db.nse_symbol || db.bse_code || (symbol || '').toUpperCase();
  const companyName = info.longName || db.company_name || stockSymbol;
  const exchange = db.nse_symbol ? (info.exchange || 'NSE') : 'BSE';
  const inWatchlist = isInWatchlist(stockSymbol);
  const handleToggleWatchlist = () => {
    if (inWatchlist) {
      const item = getWatchlistItem(stockSymbol);
      if (item) removeFromWatchlist(item.id);
    } else {
      addToWatchlist({
        symbol: stockSymbol,
        companyName,
        company_name: companyName,
        exchange,
        isin: db.isin || '',
      });
    }
  };

  const price = info.currentPrice ?? info.regularMarketPrice ?? info.previousClose;
  const change = info.regularMarketChange;
  const changePct = info.regularMarketChangePercent;
  const isUp = change >= 0;
  const handleTabChange = (nextTab) => {
    setTab(nextTab);
    const isin = db?.isin;
    if (!isin) return;
    if (nextTab === 'bse-notices') fetchBseNotices(isin);
    if (nextTab === 'documents') fetchDocuments(isin);
    if (nextTab === 'earnings') fetchEarningsTranscripts(isin);
    if (nextTab === 'xsignals') fetchXFeed(isin);
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* ── Header ── */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Button variant="ghost" size="sm" onClick={() => navigate('/public/screener')} className="text-muted-foreground px-2">
            ← Back
          </Button>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">{info.longName || db.company_name || symbol}</h1>
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {db.nse_symbol && <Badge variant="outline">{db.nse_symbol}.NS</Badge>}
              {db.bse_code && <Badge variant="outline">BSE: {db.bse_code}</Badge>}
              {!db.nse_symbol && !db.bse_code && <Badge variant="outline">{symbol?.toUpperCase()}</Badge>}
              {db.isin && <Badge variant="outline">{db.isin}</Badge>}
              {info.exchange && <span>{info.exchange}</span>}
            </div>
            {(db.ai_sector || db.ai_industry) && (
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {db.ai_sector && <Badge>{db.ai_sector}</Badge>}
                {db.ai_industry && <Badge variant="secondary">{db.ai_industry}</Badge>}
                {(db.ai_tags || []).map((t) => (
                  <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                ))}
              </div>
            )}
          </div>
          <div className="text-right lg:text-right space-y-2">
            <p className={`text-3xl font-bold ${isUp ? 'text-green-500' : 'text-red-500'}`}>
              ₹{fmt(price)}
            </p>
            {change != null && (
              <p className={`text-sm font-medium ${isUp ? 'text-green-500' : 'text-red-500'}`}>
                {isUp ? '+' : ''}{fmt(change)} ({changePct != null ? `${fmt(changePct)}%` : ''})
              </p>
            )}
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleWatchlist}
                className="gap-2"
              >
                {inWatchlist ? <StarOff className="w-4 h-4 text-yellow-500" /> : <Star className="w-4 h-4" />}
                {inWatchlist ? 'Remove from Watchlist' : 'Add to Watchlist'}
              </Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => navigate(`/public/india-company/${symbol}/pitch`)}
              >
                Generate Pitch Report
              </Button>
              <ShareToChat
                messageType="shared_research"
                payload={{
                  type: 'research',
                  title: `${info.longName || db.company_name || symbol} — Company Research`,
                  symbol: stockSymbol,
                  summary: `${db.ai_sector || ''} ${db.ai_industry ? '| ' + db.ai_industry : ''} | MCap: ${info.marketCap ? fmtCr(info.marketCap) : '—'} | P/E: ${info.trailingPE ? fmt(info.trailingPE) : '—'}`,
                  url: `/public/india-company/${symbol}`,
                }}
                defaultComment={`Check out ${info.longName || symbol}`}
              />
            </div>
          </div>
        </div>

        {/* Quick stats badges */}
        <div className="flex flex-wrap gap-3 mt-3">
          {info.marketCap && <span className="text-xs bg-muted px-2 py-1 rounded">MCap: {fmtCr(info.marketCap)}</span>}
          {info.trailingPE && <span className="text-xs bg-muted px-2 py-1 rounded">P/E: {fmt(info.trailingPE)}</span>}
          {info.priceToBook && <span className="text-xs bg-muted px-2 py-1 rounded">P/B: {fmt(info.priceToBook)}</span>}
          {info.dividendYield != null && <span className="text-xs bg-muted px-2 py-1 rounded">Div Yield: {pct(info.dividendYield)}</span>}
          {info.trailingEps && <span className="text-xs bg-muted px-2 py-1 rounded">EPS: ₹{fmt(info.trailingEps)}</span>}
        </div>

        <FiftyTwoWeekBar low={info.fiftyTwoWeekLow} high={info.fiftyTwoWeekHigh} current={price} />
      </div>

      {/* ── AI View Card ── */}
      <AIViewCard isin={db.isin} symbol={stockSymbol} companyName={companyName} />

      {/* ── Tabs ── */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="flex w-full gap-0 h-auto p-0.5">
          {[
            ['overview', 'Overview'], ['intel', 'INTEL'], ['chart', 'Chart'],
            ['financials', 'Financials'], ['shareholding', 'Holdings'],
            ['corporate-info', 'Corp Info'],
            ['credit-ratings', 'Ratings'], ['peers', 'Peers'], ['corporate-actions', 'Actions'],
            ['insider', 'Insider'], ['bse-notices', 'Notices'], ['documents', 'Docs'],
            ['earnings', 'Earnings'], ['xsignals', 'X Signals'], ['team-notes', 'Notes'],
          ].map(([val, label]) => (
            <TabsTrigger key={val} value={val} className="flex-1 text-[11px] px-1.5 py-1.5 min-w-0">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ── Tab 1: Overview ── */}
        <TabsContent value="overview">
          {isVerified ? (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 hover:bg-emerald-500/10">
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Exchange-verified
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Key metrics from NSE filings{vfRatios.period_end ? ` · as of ${vfRatios.period_end}` : ''}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <MetricCard label="Market Cap" value={fmtCr(vfSnap.market_cap)} />
                <MetricCard label="P/E" value={fmt(vfRatios.pe)} />
                <MetricCard label="P/B" value={fmt(vfRatios.pb)} />
                <MetricCard label="EPS (TTM)" value={vfRatios.eps != null ? `₹${fmt(vfRatios.eps)}` : '—'} />
                <MetricCard label="ROE" value={pctRaw(vfRatios.roe)} />
                <MetricCard label="Op. Margin" value={pctRaw(vfRatios.opm)} />
                <MetricCard label="Net Margin" value={pctRaw(vfRatios.npm)} />
                <MetricCard label="EV/EBITDA" value={fmt(vfSnap.ev_ebitda)} />
                <MetricCard label="Revenue (latest FY)" value={vfLatest.revenue != null ? `₹${fmt(vfLatest.revenue, 0)} Cr` : '—'} />
                <MetricCard label="Net Profit (latest FY)" value={vfLatest.pat != null ? `₹${fmt(vfLatest.pat, 0)} Cr` : '—'} />
                <MetricCard label="Total Borrowings" value={vfLatest.debt != null ? `₹${fmt(vfLatest.debt, 0)} Cr` : '—'} />
                <MetricCard label="Dividend Yield" value={vfSnap.dividend_yield != null ? pctRaw(vfSnap.dividend_yield) : '—'} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
              <MetricCard label="Market Cap" value={fmtCr(info.marketCap)} />
              <MetricCard label="P/E (TTM)" value={fmt(info.trailingPE)} />
              <MetricCard label="P/B" value={fmt(info.priceToBook)} />
              <MetricCard label="EPS (TTM)" value={info.trailingEps != null ? `₹${fmt(info.trailingEps)}` : '—'} />
              <MetricCard label="ROE" value={pct(info.returnOnEquity)} />
              <MetricCard label="Debt/Equity" value={fmt(info.debtToEquity)} />
              <MetricCard label="Current Ratio" value={fmt(info.currentRatio)} />
              <MetricCard label="Dividend Yield" value={pct(info.dividendYield)} />
              <MetricCard label="Book Value" value={info.bookValue != null ? `₹${fmt(info.bookValue)}` : '—'} />
              <MetricCard label="EV/EBITDA" value={fmt(info.enterpriseToEbitda)} />
              <MetricCard label="Net Margin" value={pct(info.profitMargins)} />
              <MetricCard label="Revenue" value={fmtCr(info.totalRevenue)} />
            </div>
          )}

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Price Chart (1 Year)</CardTitle></CardHeader>
            <CardContent>
              <PriceChart data={companyData.history_1y} />
            </CardContent>
          </Card>

          {(db.ai_business_desc || info.longBusinessSummary) && (
            <Card className="mt-4">
              <CardHeader className="pb-2"><CardTitle className="text-base">About</CardTitle></CardHeader>
              <CardContent>
                {db.ai_business_desc && (
                  <p className="text-sm mb-3 font-medium">{db.ai_business_desc}</p>
                )}
                {info.longBusinessSummary && (
                  <p className="text-sm text-muted-foreground leading-relaxed">{info.longBusinessSummary}</p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Tab: INTEL scorecard ── */}
        <TabsContent value="intel">
          <IntelPanel isin={db.isin} companyName={companyName} />
        </TabsContent>

        {/* ── Tab: Chart (OHLC candles + volume) ── */}
        <TabsContent value="chart">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Price Chart</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartPanel isin={db.isin} height={460} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 2: Financials (includes Ratios & Technicals) ── */}
        <TabsContent value="financials">
          {isVerified ? (
            <VerifiedFinancials data={vf} companyName={companyName} />
          ) : (
            <FinancialsTab companyData={companyData} info={info} />
          )}
        </TabsContent>

        {/* ── Tab: Corporate Info (MCA) ── */}
        <TabsContent value="corporate-info">
          <CorporateInfoSection
            mcaData={companyData.mca_data}
            charges={companyData.charges}
            directors={companyData.directors}
            db={db}
          />
        </TabsContent>

        {/* ── Tab 4: Shareholding ── */}
        <TabsContent value="shareholding">
          <ShareholdingSection
            pattern={companyData.shareholding_pattern}
            details={companyData.shareholding_details}
            promoters={companyData.promoters}
            directors={companyData.directors}
            majorHolders={companyData.major_holders}
            institutionalHolders={companyData.institutional_holders}
            shareholdingFilings={companyData.shareholding_filings}
          />
          <div className="mt-6">
            <h3 className="text-base font-semibold mb-3">Analyst Recommendations</h3>
            <RecommendationsChart data={companyData.recommendations} />
          </div>
        </TabsContent>

        {/* ── Tab: Credit Ratings ── */}
        <TabsContent value="credit-ratings">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Credit Ratings (Indian + Global Agencies)</CardTitle>
            </CardHeader>
            <CardContent>
              <CreditRatingsSection ratings={companyData.credit_ratings || []} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 5: Peers ── */}
        <TabsContent value="peers">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Peers in {db.ai_sector || 'same sector'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PeersTable peers={companyData.peers} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Corporate Actions ── */}
        <TabsContent value="corporate-actions">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Corporate Actions</CardTitle>
            </CardHeader>
            <CardContent>
              {companyData.corporate_actions?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-3 font-medium text-muted-foreground">Type</th>
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">Ex Date</th>
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">Purpose</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Amount / Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyData.corporate_actions.map((a, i) => (
                        <tr key={i} className="border-b border-muted/50">
                          <td className="py-1.5 pr-3">
                            <Badge variant={a.action_type === 'DIVIDEND' ? 'default' : 'secondary'} className="text-xs">
                              {a.action_type}
                            </Badge>
                          </td>
                          <td className="py-1.5 px-3">{a.ex_date ? new Date(a.ex_date).toLocaleDateString() : '—'}</td>
                          <td className="py-1.5 px-3">{a.purpose || '—'}</td>
                          <td className="text-right py-1.5 px-3 font-mono">
                            {a.amount != null ? `₹${fmt(a.amount)}` : a.details || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No corporate actions data available yet. Data is fetched from BSE automatically.</p>
              )}
            </CardContent>
          </Card>

          {companyData.bse_ratios && Object.values(companyData.bse_ratios).some(v => v !== null && v !== undefined) && (
            <Card className="mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">BSE Financial Ratios</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><span className="text-xs text-muted-foreground">EPS</span><div className="font-mono text-sm">{companyData.bse_ratios.eps != null ? `₹${fmt(companyData.bse_ratios.eps)}` : '—'}</div></div>
                  <div><span className="text-xs text-muted-foreground">P/E</span><div className="font-mono text-sm">{fmt(companyData.bse_ratios.pe)}</div></div>
                  <div><span className="text-xs text-muted-foreground">P/B</span><div className="font-mono text-sm">{fmt(companyData.bse_ratios.pb)}</div></div>
                  <div><span className="text-xs text-muted-foreground">ROE</span><div className="font-mono text-sm">{companyData.bse_ratios.roe != null ? `${fmt(companyData.bse_ratios.roe)}%` : '—'}</div></div>
                  <div><span className="text-xs text-muted-foreground">OPM</span><div className="font-mono text-sm">{companyData.bse_ratios.opm != null ? `${fmt(companyData.bse_ratios.opm)}%` : '—'}</div></div>
                  <div><span className="text-xs text-muted-foreground">NPM</span><div className="font-mono text-sm">{companyData.bse_ratios.npm != null ? `${fmt(companyData.bse_ratios.npm)}%` : '—'}</div></div>
                  <div><span className="text-xs text-muted-foreground">Con. EPS</span><div className="font-mono text-sm">{companyData.bse_ratios.con_eps != null ? `₹${fmt(companyData.bse_ratios.con_eps)}` : '—'}</div></div>
                  <div><span className="text-xs text-muted-foreground">Con. P/E</span><div className="font-mono text-sm">{fmt(companyData.bse_ratios.con_pe)}</div></div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Tab: Insider Trading / ESOP ── */}
        <TabsContent value="insider">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Insider Trading, SAST & ESOP Disclosures</CardTitle>
            </CardHeader>
            <CardContent>
              {companyData.insider_trading?.length ? (
                <div className="space-y-2">
                  {companyData.insider_trading.map((item, i) => (
                    <div key={i} className="border rounded-md p-3 text-sm hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Badge variant={item.category === 'insider' ? 'destructive' : item.category === 'esop' ? 'default' : 'secondary'} className="text-xs">
                            {item.category === 'insider' ? 'Insider Trading' : item.category === 'esop' ? 'ESOP' : 'Acquisition'}
                          </Badge>
                          <span className="font-medium">{item.headline || 'Disclosure'}</span>
                        </div>
                        {item.attachment_url && (
                          <a
                            href={item.attachment_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-emerald-600 hover:text-emerald-700 whitespace-nowrap shrink-0"
                          >
                            View PDF ↗
                          </a>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {item.date ? new Date(item.date).toLocaleDateString() : ''}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No insider trading or ESOP disclosures available yet. Data is fetched from BSE automatically.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: BSE Notices ── */}
        <TabsContent value="bse-notices">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">BSE Corporate Notices & Filings</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingBseNotices ? (
                <p className="text-sm text-muted-foreground">Loading BSE notices...</p>
              ) : (() => {
                const notices = bseNotices.length > 0 ? bseNotices : (companyData.bse_notices || []);
                if (!notices.length) {
                  return <p className="text-sm text-muted-foreground">No BSE notices available yet. Data is fetched automatically from BSE India for all listed companies.</p>;
                }
                const CATEGORY_LABELS = {
                  board_meeting: 'Board Meeting',
                  board_meeting_schedule: 'Board Meeting Schedule',
                  result: 'Financial Results',
                  agm_egm: 'AGM / EGM',
                  company_update: 'Company Update',
                  annual_report: 'Annual Report',
                  credit_rating: 'Credit Rating',
                  corp_governance: 'Corp. Governance',
                  dividend: 'Dividend',
                  bonus: 'Bonus',
                  scheme: 'Scheme of Arrangement',
                  buyback: 'Buyback',
                  preferential: 'Preferential Issue',
                  director_change: 'Director Change',
                };
                const grouped = {};
                notices.forEach((n) => {
                  const key = n.category || 'other';
                  if (!grouped[key]) grouped[key] = [];
                  grouped[key].push(n);
                });
                const catOrder = [
                  'result', 'board_meeting', 'board_meeting_schedule', 'annual_report',
                  'agm_egm', 'company_update', 'dividend', 'bonus', 'credit_rating',
                  'corp_governance', 'director_change', 'scheme', 'buyback', 'preferential',
                ];
                const sortedCats = Object.keys(grouped).sort(
                  (a, b) => (catOrder.indexOf(a) === -1 ? 99 : catOrder.indexOf(a)) - (catOrder.indexOf(b) === -1 ? 99 : catOrder.indexOf(b))
                );
                return (
                  <div className="space-y-6">
                    <p className="text-xs text-muted-foreground">
                      Showing {notices.length} notices across {sortedCats.length} categories
                    </p>
                    {sortedCats.map((cat) => (
                      <div key={cat}>
                        <h4 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
                          {CATEGORY_LABELS[cat] || cat.replace(/_/g, ' ')} ({grouped[cat].length})
                        </h4>
                        <div className="space-y-1.5">
                          {grouped[cat].map((item, i) => (
                            <div key={item.id || i} className="border rounded-md p-3 text-sm hover:bg-muted/50 transition-colors">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <span className="font-medium line-clamp-2">{item.headline || 'Notice'}</span>
                                  {item.subcategory && (
                                    <span className="ml-2 text-xs text-muted-foreground">({item.subcategory})</span>
                                  )}
                                </div>
                                {item.attachment_url && (
                                  <a
                                    href={item.attachment_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs text-emerald-600 hover:text-emerald-700 whitespace-nowrap shrink-0 flex items-center gap-1"
                                  >
                                    <ExternalLink className="h-3 w-3" /> PDF
                                  </a>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {item.date ? new Date(item.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span>Company Documents</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startIngestion(db?.isin ? [db.isin] : null)}
                >
                  Re-fetch this company
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingDocuments ? (
                <p className="text-sm text-muted-foreground">Loading documents...</p>
              ) : documents.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Documents for all listed companies are fetched automatically and stored in the database. If this company has no documents yet, ingestion may still be in progress or re-fetch this company above.
                </p>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div key={doc.id} className="border rounded-md p-3 text-sm hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium">{doc.title}</div>
                        {doc.url && (
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-emerald-600 hover:text-emerald-700 whitespace-nowrap shrink-0"
                          >
                            Open document ↗
                          </a>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {doc.source_name || 'Unknown source'} · {doc.doc_type}
                        {doc.published_at ? ` · ${new Date(doc.published_at).toLocaleDateString()}` : ''}
                        {typeof doc.chunk_count === 'number' ? ` · ${doc.chunk_count} chunks` : ''}
                        {doc.byte_size ? ` · ${(doc.byte_size / 1024).toFixed(0)} KB` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="earnings">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Earnings Call Transcripts (Last 2 Years)</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingTranscripts ? (
                <p className="text-sm text-muted-foreground">Loading transcripts...</p>
              ) : transcripts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No transcripts available yet. Earnings calls are ingested for all companies automatically; re-fetch this company in the Documents tab if needed.
                </p>
              ) : (
                <div className="space-y-3">
                  {transcripts.map((t) => (
                    <div key={t.id} className="border rounded-md p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{t.quarter} FY{t.fiscal_year}</Badge>
                        {t.call_date && <span className="text-xs text-muted-foreground">{new Date(t.call_date).toLocaleDateString()}</span>}
                      </div>
                      <div className="font-medium text-sm mt-1">{t.title || 'Earnings Call Transcript'}</div>
                      {t.excerpt && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{t.excerpt}</p>
                      )}
                      {t.source_url && (
                        <a
                          href={t.source_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-emerald-600 hover:text-emerald-700 mt-2 inline-block"
                        >
                          Open source
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 8: X Signals ── */}
        <TabsContent value="xsignals">
          <XSignalsFeed
            posts={xPosts}
            loading={xLoading}
            refreshing={xRefreshing}
            error={xError}
            lastFetched={xLastFetched}
            sourceCount={xSourceCount}
            feedMode={xFeedMode}
            onFeedModeChange={setXFeedMode}
            onRefresh={() => db?.isin && refreshXFeed(db.isin)}
          />
        </TabsContent>

        <TabsContent value="team-notes">
          <TeamNotesTab isin={db.isin} symbol={stockSymbol} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── X Signals Feed Component ───────────────────────────

function XSignalsFeed({ posts, loading, refreshing, error, lastFetched, sourceCount, feedMode, onFeedModeChange, onRefresh }) {
  const [credibleOnly, setCredibleOnly] = useState(false);
  const [selectedTier, setSelectedTier] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [minFollowers, setMinFollowers] = useState('any');
  const [sortBy, setSortBy] = useState('newest');

  const allTags = useMemo(() => {
    const tagSet = new Set();
    (posts || []).forEach((p) => (p.tags || []).forEach((t) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [posts]);

  const filteredPosts = useMemo(() => {
    let result = posts || [];

    // Source mode filter
    if (feedMode === 'curated') {
      result = result.filter((p) => p.fetch_mode === 'whitelist' || p.fetch_mode === 'fallback');
    } else if (feedMode === 'discovery') {
      result = result.filter((p) => p.fetch_mode === 'discovery');
    }

    if (credibleOnly) {
      result = result.filter((p) => p.source_tier === 'A' || p.source_tier === 'B' || (p.credibility_score && p.credibility_score >= 0.7));
    }
    if (selectedTier !== 'all') {
      result = result.filter((p) => p.source_tier === selectedTier);
    }
    if (selectedTag !== 'all') {
      result = result.filter((p) => (p.tags || []).includes(selectedTag));
    }

    // Follower filter
    if (minFollowers !== 'any') {
      const threshold = parseInt(minFollowers, 10);
      result = result.filter((p) => (p.author_followers || 0) >= threshold);
    }

    // Sort
    if (sortBy === 'followers') {
      result = [...result].sort((a, b) => (b.author_followers || 0) - (a.author_followers || 0));
    } else if (sortBy === 'engagement') {
      const eng = (p) => (p.metrics?.likes ?? p.like_count ?? 0) + (p.metrics?.reposts ?? p.repost_count ?? 0) * 2;
      result = [...result].sort((a, b) => eng(b) - eng(a));
    }

    return result;
  }, [posts, feedMode, credibleOnly, selectedTier, selectedTag, minFollowers, sortBy]);

  const discoveryCount = useMemo(() => (posts || []).filter((p) => p.fetch_mode === 'discovery').length, [posts]);
  const curatedCount = useMemo(() => (posts || []).filter((p) => p.fetch_mode !== 'discovery').length, [posts]);

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const tierColor = (tier) => {
    if (tier === 'A') return 'bg-emerald-600 text-white';
    if (tier === 'B') return 'bg-blue-600 text-white';
    return 'bg-muted text-muted-foreground';
  };

  const formatFollowers = (count) => {
    if (!count && count !== 0) return null;
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
    return String(count);
  };

  const hasActiveFilters = credibleOnly || selectedTier !== 'all' || selectedTag !== 'all' || minFollowers !== 'any' || sortBy !== 'newest';

  const clearAll = () => {
    setCredibleOnly(false);
    setSelectedTier('all');
    setSelectedTag('all');
    setMinFollowers('any');
    setSortBy('newest');
  };

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold">Live X Signals</h3>
          {sourceCount > 0 && (
            <span className="text-xs text-muted-foreground">{sourceCount} curated sources</span>
          )}
          {discoveryCount > 0 && (
            <span className="text-xs text-muted-foreground">+ {discoveryCount} discovered</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lastFetched && (
            <span className="text-xs text-muted-foreground">
              Updated {timeAgo(lastFetched)}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            className="gap-1.5"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Source mode tabs */}
      <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/50 w-fit">
        {[
          { key: 'all', label: 'All Sources', count: (posts || []).length },
          { key: 'curated', label: 'Curated', count: curatedCount },
          { key: 'discovery', label: 'Discovery', count: discoveryCount, icon: Search },
        ].map(({ key, label, count, icon: Icon }) => (
          <button
            key={key}
            onClick={() => onFeedModeChange(key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              feedMode === key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {Icon && <Icon className="h-3 w-3" />}
            {label}
            {count > 0 && <span className="text-[10px] opacity-60">({count})</span>}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setCredibleOnly(!credibleOnly)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            credibleOnly
              ? 'bg-emerald-600 text-white border-emerald-600'
              : 'bg-background border-border hover:bg-muted'
          }`}
        >
          <ShieldCheck className="h-3.5 w-3.5" />
          Credible Only
        </button>
        <select
          value={selectedTier}
          onChange={(e) => setSelectedTier(e.target.value)}
          className="text-xs px-2 py-1.5 rounded-md border bg-background"
        >
          <option value="all">All Tiers</option>
          <option value="A">Tier A (Official)</option>
          <option value="B">Tier B (Media)</option>
          <option value="C">Tier C (Analysts)</option>
        </select>
        <select
          value={minFollowers}
          onChange={(e) => setMinFollowers(e.target.value)}
          className="text-xs px-2 py-1.5 rounded-md border bg-background"
        >
          <option value="any">Any followers</option>
          <option value="1000">1K+ followers</option>
          <option value="5000">5K+ followers</option>
          <option value="10000">10K+ followers</option>
          <option value="50000">50K+ followers</option>
          <option value="100000">100K+ followers</option>
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="text-xs px-2 py-1.5 rounded-md border bg-background"
        >
          <option value="newest">Newest first</option>
          <option value="engagement">Most engaged</option>
          <option value="followers">Most followers</option>
        </select>
        {allTags.length > 0 && (
          <select
            value={selectedTag}
            onChange={(e) => setSelectedTag(e.target.value)}
            className="text-xs px-2 py-1.5 rounded-md border bg-background"
          >
            <option value="all">All Tags</option>
            {allTags.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        )}
        {hasActiveFilters && (
          <button
            onClick={clearAll}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Content */}
      {error && (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-24" /></CardContent></Card>
          ))}
        </div>
      ) : !error && filteredPosts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-sm text-muted-foreground">
              {posts.length > 0
                ? 'No posts match your current filters.'
                : 'No X signals found for this company. Try refreshing to fetch the latest posts.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredPosts.map((post) => (
            <Card key={post.post_id} className="hover:bg-muted/20 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Author line */}
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="font-medium text-sm truncate">{post.author_name || post.author_handle}</span>
                      <span className="text-xs text-muted-foreground">@{post.author_handle}</span>
                      {post.source_tier && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${tierColor(post.source_tier)}`}>
                          Tier {post.source_tier}
                        </span>
                      )}
                      {post.fetch_mode === 'discovery' && !post.source_tier && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-amber-500/15 text-amber-600">
                          Discovery
                        </span>
                      )}
                      {post.author_followers != null && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                          <Users className="h-3 w-3" />
                          {formatFollowers(post.author_followers)}
                        </span>
                      )}
                      {post.credibility_score != null && (
                        <span className="text-[10px] text-muted-foreground">
                          {Math.round(post.credibility_score * 100)}% credible
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">
                        {timeAgo(post.created_at)}
                      </span>
                    </div>

                    {/* Post text */}
                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{post.text}</p>

                    {/* Tags + metrics */}
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {(post.tags || []).map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">{tag}</Badge>
                      ))}
                      <span className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
                        <Heart className="h-3 w-3" /> {post.metrics?.likes ?? post.like_count ?? 0}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Repeat2 className="h-3 w-3" /> {post.metrics?.reposts ?? post.repost_count ?? 0}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MessageCircle className="h-3 w-3" /> {post.metrics?.replies ?? post.reply_count ?? 0}
                      </span>
                    </div>
                  </div>

                  {/* Open on X */}
                  {post.url && (
                    <a
                      href={post.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-shrink-0 p-2 rounded-md hover:bg-muted transition-colors"
                      title="Open on X"
                    >
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Financials Sub-tab Component ───────────────────────

function FinancialsTab({ companyData, info = {} }) {
  const [subTab, setSubTab] = React.useState('pnl');
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { key: 'pnl', label: 'Profit & Loss' },
          { key: 'bs', label: 'Balance Sheet' },
          { key: 'cf', label: 'Cash Flow' },
          { key: 'ratios', label: 'Ratios & Technicals' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              subTab === t.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'pnl' && (
        <>
          <FinancialTable data={companyData.financials} title="Annual P&L" />
          <div className="mt-6">
            <FinancialTable data={companyData.quarterly_financials} title="Quarterly P&L" />
          </div>
          <RevenueProfitChart financials={companyData.financials} />
        </>
      )}
      {subTab === 'bs' && (
        <>
          <FinancialTable data={companyData.balance_sheet} title="Annual Balance Sheet" />
          <div className="mt-6">
            <FinancialTable data={companyData.quarterly_balance_sheet} title="Quarterly Balance Sheet" />
          </div>
        </>
      )}
      {subTab === 'cf' && (
        <>
          <FinancialTable data={companyData.cashflow} title="Annual Cash Flow" />
          <div className="mt-6">
            <FinancialTable data={companyData.quarterly_cashflow} title="Quarterly Cash Flow" />
          </div>
        </>
      )}
      {subTab === 'ratios' && (
        <RatiosTechnicalsTab info={info} history5y={companyData.history_5y} />
      )}
    </div>
  );
}

// ─── Ratios & Technicals Tab ────────────────────────────

function RatiosTechnicalsTab({ info, history5y }) {
  const valuation = [
    { label: 'P/E (TTM)', value: fmt(info.trailingPE) },
    { label: 'P/E (Forward)', value: fmt(info.forwardPE) },
    { label: 'P/B', value: fmt(info.priceToBook) },
    { label: 'P/S', value: fmt(info.priceToSalesTrailing12Months) },
    { label: 'EV/EBITDA', value: fmt(info.enterpriseToEbitda) },
    { label: 'EV/Revenue', value: fmt(info.enterpriseToRevenue) },
    { label: 'PEG Ratio', value: fmt(info.pegRatio) },
  ];

  const profitability = [
    { label: 'ROE', value: pct(info.returnOnEquity) },
    { label: 'ROA', value: pct(info.returnOnAssets) },
    { label: 'Gross Margin', value: pct(info.grossMargins) },
    { label: 'Operating Margin', value: pct(info.operatingMargins) },
    { label: 'Net Margin', value: pct(info.profitMargins) },
    { label: 'EBITDA Margin', value: pct(info.ebitdaMargins) },
  ];

  const health = [
    { label: 'Current Ratio', value: fmt(info.currentRatio) },
    { label: 'Quick Ratio', value: fmt(info.quickRatio) },
    { label: 'Debt/Equity', value: fmt(info.debtToEquity) },
    { label: 'Total Debt', value: fmtCr(info.totalDebt) },
    { label: 'Total Cash', value: fmtCr(info.totalCash) },
    { label: 'Free Cash Flow', value: fmtCr(info.freeCashflow) },
  ];

  const technicals = [
    { label: '50-Day MA', value: info.fiftyDayAverage != null ? `₹${fmt(info.fiftyDayAverage)}` : '—' },
    { label: '200-Day MA', value: info.twoHundredDayAverage != null ? `₹${fmt(info.twoHundredDayAverage)}` : '—' },
    { label: 'Beta', value: fmt(info.beta) },
    { label: 'Avg Volume (10d)', value: fmt(info.averageVolume10days, 0) },
    { label: 'Avg Volume (3m)', value: fmt(info.averageVolume, 0) },
    { label: 'Short Ratio', value: fmt(info.shortRatio) },
  ];

  return (
    <div className="space-y-6">
      <RatioSection title="Valuation" items={valuation} />
      <RatioSection title="Profitability" items={profitability} />
      <RatioSection title="Financial Health" items={health} />
      <RatioSection title="Technical Indicators" items={technicals} />
      {history5y && history5y.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Price Chart (5 Year)</CardTitle></CardHeader>
          <CardContent>
            <PriceChart data={history5y} height={280} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function RatioSection({ title, items }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2">
          {items.map((item) => (
            <div key={item.label} className="flex justify-between py-1 border-b border-muted/50">
              <span className="text-sm text-muted-foreground">{item.label}</span>
              <span className="text-sm font-medium">{item.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
